import { useEffect, useRef } from 'react';
import queryString from 'query-string';
import type { ParsedQuery } from 'query-string';
import { PageKind } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import globalWindow from '@shared/core/globals';

type UseRegistryLinksRedirectArgs = Readonly<{
  eventHandle: string;
  currentPageSlug: string | undefined;
  isUrlBasedCustomPageSlug: boolean;
  currentPageKind: Maybe<PageKind>;
  queryParams: ParsedQuery;
}>;

/**
 * While `registryEnableGuestNavRegistryLinks` is off, the registry lives in the standalone registry
 * app at `/<handle>/registry`, which only a full page load reaches. Sends /registrylinks (and
 * /page/registry) there once, replacing the history entry so Back doesn't bounce off it again.
 * User-created pages on those slugs are ordinary custom pages and stay put.
 */
export const useRegistryLinksRedirect = ({ eventHandle, currentPageSlug, isUrlBasedCustomPageSlug, currentPageKind, queryParams }: UseRegistryLinksRedirectArgs) => {
  const [isRegistryLinksNavEnabled, isRegistryLinksNavLoading] = useIsFeatureEnabled('registryEnableGuestNavRegistryLinks');

  const isRegistrySlug = currentPageSlug === 'registrylinks' || currentPageSlug === 'registry';
  // The router serves /<handle>/registry from the registry app, so the layout never renders there today.
  const isAlreadyOnRegistry = currentPageSlug === 'registry' && !isUrlBasedCustomPageSlug;
  // Waits for the flag: treatment users redirected before Amplitude resolves would land in the registry app for good.
  const shouldRedirect = isRegistrySlug && !isAlreadyOnRegistry && !isRegistryLinksNavLoading && !isRegistryLinksNavEnabled && currentPageKind !== PageKind.userCustom;

  const redirectSource = `${eventHandle}/${isUrlBasedCustomPageSlug ? 'page/' : ''}${currentPageSlug}`;
  const redirectedFromRef = useRef<string | null>(null);

  useEffect(() => {
    // Keyed to the page it fired from, so leaving it in-SPA and coming back can redirect again.
    if (redirectedFromRef.current !== redirectSource) {
      redirectedFromRef.current = null;
    }
    if (!shouldRedirect || redirectedFromRef.current) {
      return;
    }
    const location = globalWindow.location;
    if (!location) {
      return;
    }
    location.replace(queryString.stringifyUrl({ url: `/${eventHandle}/registry`, query: queryParams }));
    redirectedFromRef.current = redirectSource;
  }, [shouldRedirect, redirectSource, eventHandle, queryParams]);

  return {
    /** The layout's own page redirects must wait while the flag loads and while the registry redirect is due. */
    shouldHoldPageRedirects: isRegistrySlug && !isAlreadyOnRegistry && (isRegistryLinksNavLoading || shouldRedirect),
  };
};
